/* Read the product's current theme block, so cart and product settings cannot drift. */
(() => {
  if (customElements.get('cart-dispatch-estimate')) return;
  const requests = new Map();
  async function readSettings(path) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product URL');
    // Explicit preview selection survives product reads during theme QA.
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const key = url.href;
    const existing = requests.get(key);
    if (existing && Date.now() - existing.time < 60000) return existing.promise;
    const promise = (async () => {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Product settings unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = html.querySelector('dispatch-estimate[data-min-days][data-max-days]');
      if (!node) return null;
      const min = Number(node.dataset.minDays), max = Number(node.dataset.maxDays);
      if (!node.dataset.minDays?.trim() || !node.dataset.maxDays?.trim() ||
          ![min, max].every(n => Number.isInteger(n) && n >= 0 && n <= 60) || max < min) return null;
      return { min, max, business: node.dataset.businessDays === 'true', locale: node.dataset.locale || document.documentElement.lang || 'ja' };
    })();
    requests.set(key, { time: Date.now(), promise });
    try { return await promise; } catch (error) { requests.delete(key); throw error; }
  }
  class CartDispatchEstimate extends HTMLElement {
    connectedCallback() {
      this.refresh = this.refresh.bind(this);
      this.refresh();
      this.timer = setInterval(this.refresh, 60000);
      document.addEventListener('visibilitychange', this.refresh);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.refresh);
    }
    async refresh() {
      if (document.hidden || this.pending) return;
      this.pending = true;
      try {
        const settings = await readSettings(this.dataset.productUrl);
        if (!this.isConnected) return;
        if (!settings) { this.replaceChildren(); this.hidden = true; return; }
        const estimate = document.createElement('dispatch-estimate');
        estimate.dataset.minDays = settings.min;
        estimate.dataset.maxDays = settings.max;
        estimate.dataset.locale = settings.locale;
        const label = document.createElement('span');
        label.setAttribute('data-dispatch-date', '');
        estimate.append(label);
        this.replaceChildren(estimate);
        this.hidden = false;
      } catch (_) {
        // Never show a guessed or stale shipping promise after a failed refresh.
        this.replaceChildren();
        this.hidden = true;
      } finally { this.pending = false; }
    }
  }
  // Persist the displayed range as private line properties before normal cart checkout.
  // Merge existing properties so uploaded artwork and customization are preserved.
  let saving = false;
  let approvedForm = null;
  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (approvedForm === form) { approvedForm = null; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (saving) return;
    saving = true;
    button.disabled = true;
    let errorNode = form.querySelector('[data-dispatch-error]');
    if (errorNode) errorNode.remove();
    const root = window.Shopify?.routes?.root || '/';
    const request = async (path, options) => {
      const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), ...options });
      if (!response.ok) throw new Error('Cart update failed');
      return response.json();
    };
    try {
      requests.clear();
      const cart = await request('cart.js');
      const planned = await Promise.all(cart.items.map(async item => {
        if (!/^(C-|S-)/.test(item.sku || '') && !(item.sku || '').includes('FRM-')) return null;
        return { item, settings: await readSettings(item.url) };
      }));
      const day = 86400000;
      const today = Math.floor((Date.now() + 9 * 3600000) / day) * day;
      for (const entry of planned) {
        if (!entry) continue;
        const { item, settings } = entry;
        const properties = { ...item.properties };
        for (const key of ['_dispatch_start', '_dispatch_end', '_dispatch_recorded_at']) delete properties[key];
        if (settings) {
          // Business days skip Saturday and Sunday (JST), matching the product page.
          const addDays = count => {
            if (!settings.business) return today + count * day;
            let t = today;
            while (count > 0) { t += day; const w = new Date(t).getUTCDay(); if (w !== 0 && w !== 6) count--; }
            return t;
          };
          properties._dispatch_start = new Date(addDays(settings.min)).toISOString().slice(0, 10);
          properties._dispatch_end = new Date(addDays(settings.max)).toISOString().slice(0, 10);
        } else if (!item.properties?._dispatch_start && !item.properties?._dispatch_end) continue;
        await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }) });
      }
      button.disabled = false;
      approvedForm = form;
      form.requestSubmit(button);
    } catch (_) {
      approvedForm = null;
      errorNode = document.createElement('p');
      errorNode.dataset.dispatchError = '';
      errorNode.setAttribute('role', 'alert');
      const lang = document.documentElement.lang;
      errorNode.textContent = lang.startsWith('ja') ? '発送予定日の保存に失敗しました。もう一度お試しください。'
        : lang.startsWith('ko') ? '발송 예정일을 저장하지 못했습니다. 다시 시도해 주세요.'
        : lang.startsWith('zh') ? '無法儲存預計出貨日期，請再試一次。'
        : 'Unable to save the estimated dispatch date. Please try again.';
      form.append(errorNode);
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } finally {
      button.disabled = false;
      saving = false;
    }
  }, true);
  customElements.define('cart-dispatch-estimate', CartDispatchEstimate);
})();
